import React, { useState } from "react";
import { Menu, ChevronDown, Eye, EyeOff } from "lucide-react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectCurrentCompany, logout } from "../../redux/redux/auth";
import logo from "../../assets/logo2.png";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { NavigationMenu, NavigationMenuItem, NavigationMenuList } from "@/components/ui/navigation-menu";
import { useGet_company_by_idQuery, useUpdateCompanyMutation } from "@/redux/api/vehicle";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "react-toastify";
import ContactModal from "./ContactModal";

const TABS = [
  { name: "Dashboard", link: "dashboard" },
  { name: "Location", link: "fastag" },
  { name: "Vehicle RC", link: "vahan" },
  { name: "Owner History", link: "vehicle-owner" },
  { name: "DL Check", link: "sarathi" },
  { name: "My Fleet", link: "MyVehicles" },
];

const Header = () => {
  const user = useSelector(selectCurrentCompany);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ newPassword: "", confirmPassword: "" });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname.split("/")[1];

  const company_id = user?._id;
  const { data: CompanyData, isLoading: CompanyLoading, error: companyError } = useGet_company_by_idQuery({ id: company_id }, { skip: !company_id });
  const company = CompanyData;
  const [updatePassword] = useUpdateCompanyMutation();

  const handleLogout = () => { dispatch(logout()); navigate("/"); };
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const handlePasswordChange = (e) => setPasswordData({ ...passwordData, [e.target.name]: e.target.value });

  const handleResetPassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) { toast.error("Passwords do not match."); return; }
    try {
      await updatePassword({ _id: company_id, password: passwordData.newPassword }).unwrap();
      toast.success("Password updated."); setIsResetPasswordOpen(false); setPasswordData({ newPassword: "", confirmPassword: "" });
    } catch { toast.error("Failed to update password."); }
  };

  return (
    <>
      <header className="w-full sticky top-0 z-50 bg-white border-b border-[#E2E8F0] h-14">
        <div className="flex h-full items-center px-4 md:px-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8 text-[#475569]" onClick={toggleMenu}><Menu className="h-5 w-5" /></Button>
            <NavLink to="/" className="flex items-center gap-2">
              <img src={logo} alt="Logo" className="h-8 w-8 object-contain" />
              <span className="text-[15px] font-bold text-[#0F172A]">Fastag Tracking</span>
            </NavLink>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <NavigationMenu className="hidden lg:flex mx-auto">
              <NavigationMenuList className="gap-0.5">
                {TABS.map((tab) => (
                  <NavigationMenuItem key={tab.name}>
                    <NavLink to={`/${tab.link}`} className={({ isActive }) => cn(
                      "px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors",
                      isActive ? "bg-[#1E40AF] text-white" : "text-[#475569] hover:text-[#1E40AF] hover:bg-[#F8FAFC]"
                    )}>{tab.name}</NavLink>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
            <Button className="h-8 px-3 text-[12px] font-semibold bg-[#D97706] hover:bg-[#B45309] text-white rounded-lg lg:flex hidden" onClick={() => navigate("/pricing")}>Upgrade</Button>
            <div className="lg:flex hidden"><ContactModal /></div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center cursor-pointer gap-1.5 pl-2">
                  <Avatar className="w-8 h-8"><AvatarFallback className="bg-[#1E40AF] text-white text-[12px] font-semibold">{user?.name?.[0] || "U"}</AvatarFallback></Avatar>
                  <span className="hidden lg:block text-[13px] font-medium text-[#0F172A]">{user?.name || "User"}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-[#94A3B8]" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 p-3 bg-white rounded-xl shadow-lg border border-[#E2E8F0]">
                <div className="text-center font-semibold text-[#0F172A] lg:hidden mb-3 text-sm">{user?.name || "User"}</div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="text-center p-2.5 bg-[#F8FAFC] rounded-lg">
                    <div className="text-[11px] text-[#1E40AF] font-medium">API Hits</div>
                    {CompanyLoading ? <Skeleton className="h-4 w-10 mx-auto mt-1" /> : companyError ? <div className="text-[11px] text-[#DC2626]">Error</div> : <div className="font-bold text-[#0F172A] text-sm">{company?.apiHit || 0}</div>}
                  </div>
                  <div className="text-center p-2.5 bg-[#F8FAFC] rounded-lg">
                    <div className="text-[11px] text-[#1E40AF] font-medium">Max Hits</div>
                    {CompanyLoading ? <Skeleton className="h-4 w-10 mx-auto mt-1" /> : companyError ? <div className="text-[11px] text-[#DC2626]">Error</div> : <div className="font-bold text-[#0F172A] text-sm">{company?.maxApiHit || 0}</div>}
                  </div>
                </div>
                <div className="space-y-1">
                  <Button variant="outline" className="w-full h-8 text-[12px] rounded-lg border-[#E2E8F0] justify-center" onClick={() => navigate("/pricing")}>{company?.planType || "N/A"} — {company?.planName || "N/A"}</Button>
                  <Button variant="outline" className="w-full h-8 text-[12px] rounded-lg border-[#E2E8F0] justify-center" onClick={() => setIsResetPasswordOpen(true)}>Reset Password</Button>
                  <Button variant="outline" className="w-full h-8 text-[12px] rounded-lg border-[#E2E8F0] justify-center" asChild><Link to="/searchhistory">Search History</Link></Button>
                  <Button variant="outline" className="w-full h-8 text-[12px] rounded-lg border-[#E2E8F0] justify-center" asChild><Link to="/transaction">Transaction History</Link></Button>
                  <Button className="w-full h-8 mt-1.5 text-[12px] bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-lg" onClick={handleLogout}>Log Out</Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent side="left" className="w-64 bg-white border-r border-[#E2E8F0]">
          <SheetHeader className="p-4 border-b border-[#E2E8F0]"><SheetTitle><img src={logo} alt="Logo" className="h-10 object-contain" /></SheetTitle></SheetHeader>
          <nav className="flex flex-col p-3 gap-0.5">
            {TABS.map((tab) => (
              <Button asChild key={tab.name} variant="ghost" className={cn(
                "text-left justify-start text-[13px] text-[#475569] font-medium hover:bg-[#F8FAFC] hover:text-[#1E40AF] rounded-lg py-2.5 px-3",
                pathName === tab.link && "bg-[#1E40AF] text-white hover:bg-[#1E3A8A] hover:text-white"
              )}><NavLink to={`/${tab.link}`} onClick={toggleMenu}>{tab.name}</NavLink></Button>
            ))}
            <Button className="mt-2 h-9 text-[13px] bg-[#D97706] hover:bg-[#B45309] text-white rounded-lg" onClick={() => { navigate("/pricing"); toggleMenu(); }}>Upgrade</Button>
            <Button asChild variant="ghost" className="text-left justify-start text-[13px] text-[#475569] font-medium hover:bg-[#F8FAFC] rounded-lg py-2.5 px-3"><ContactModal /></Button>
          </nav>
        </SheetContent>
      </Sheet>

      <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>
        <DialogContent className="rounded-2xl max-w-[400px]">
          <DialogHeader><DialogTitle className="text-lg font-bold text-[#0F172A]">Reset Password</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-3">
            <div className="grid gap-1.5">
              <Label htmlFor="newPassword" className="text-[13px] font-medium text-[#475569]">New Password</Label>
              <div className="relative">
                <Input id="newPassword" name="newPassword" type={showNewPassword ? "text" : "password"} value={passwordData.newPassword} onChange={handlePasswordChange} className="rounded-lg border-[#E2E8F0] h-10" required />
                <Button type="button" variant="ghost" size="icon" className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setShowNewPassword(!showNewPassword)}>{showNewPassword ? <EyeOff className="h-4 w-4 text-[#94A3B8]" /> : <Eye className="h-4 w-4 text-[#94A3B8]" />}</Button>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="confirmPassword" className="text-[13px] font-medium text-[#475569]">Confirm Password</Label>
              <div className="relative">
                <Input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? "text" : "password"} value={passwordData.confirmPassword} onChange={handlePasswordChange} className="rounded-lg border-[#E2E8F0] h-10" required />
                <Button type="button" variant="ghost" size="icon" className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? <EyeOff className="h-4 w-4 text-[#94A3B8]" /> : <Eye className="h-4 w-4 text-[#94A3B8]" />}</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResetPasswordOpen(false)} className="rounded-lg border-[#E2E8F0] text-[13px]">Cancel</Button>
            <Button onClick={handleResetPassword} className="rounded-lg bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-[13px]">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default Header;
